import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import feather from 'feather-icons';
import 'bootstrap/dist/css/bootstrap.min.css';

const TimeUtils = {
  // Convert Unix timestamp to location's time
  toLocationTime: (unixTimestamp: number, timezoneOffset: number): Date => {
    // Create date in UTC from Unix timestamp
    const date = new Date(unixTimestamp * 1000);

    // Get browser's timezone offset in minutes
    const browserOffset = date.getTimezoneOffset() * 60;

    // Create new date with the location's timezone
    return new Date((unixTimestamp + timezoneOffset + browserOffset) * 1000);
  },

  // Get current time in location's timezone
  getCurrentLocationTime: (timezoneOffset: number): Date => {
    const now = Math.floor(Date.now() / 1000);
    return TimeUtils.toLocationTime(now, timezoneOffset);
  },

  // Format time (e.g., "3:00 PM")
  formatTime: (date: Date): string => {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  },

  // Format hour only (e.g., "3 PM")
  formatHour: (date: Date): string => {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      hour12: true
    });
  },

  // Get time of day for background
  getTimeOfDay: (sunriseTimestamp: number, sunsetTimestamp: number, timezoneOffset: number): 'night' | 'day' | 'dawn' | 'dusk' => {
    const currentLocationTime = Date.now() / 1000 + timezoneOffset;
    const sunriseBuffer = 30 * 60; // 30 minutes in seconds
    const sunsetBuffer = 30 * 60;

    if (currentLocationTime < sunriseTimestamp - sunriseBuffer) return 'night';
    if (currentLocationTime >= sunriseTimestamp - sunriseBuffer && currentLocationTime <= sunriseTimestamp + sunriseBuffer) return 'dawn';
    if (currentLocationTime > sunriseTimestamp + sunriseBuffer && currentLocationTime < sunsetTimestamp - sunsetBuffer) return 'day';
    if (currentLocationTime >= sunsetTimestamp - sunsetBuffer && currentLocationTime <= sunsetTimestamp + sunsetBuffer) return 'dusk';
    return 'night';
  },

  // Format last updated time
  formatLastUpdated: (date: Date): string => {
    return date.toLocaleString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  },

  // Add these new functions
  formatDay: (date: Date): string => {
    return date.toLocaleDateString([], {
      weekday: 'long'  // e.g., "Monday"
    });
  },

  formatDate: (date: Date): string => {
    return date.toLocaleDateString([], {
      weekday: 'short',  // e.g., "Mon"
      month: 'short',    // e.g., "Jan"
      day: 'numeric'     // e.g., "15"
    });
  },
};

const WeatherUtils = {
  // Temperature conversions (data comes in Celsius)
  convertTemperature: (temp: number, unit: string): number => {
    if (unit === 'us') return (temp * 9/5) + 32; // Convert Celsius to Fahrenheit
    return temp; // Keep as Celsius
  },

  // Wind speed conversions (data comes in km/h)
  convertWindSpeed: (speed: number, unit: string): number => {
    switch (unit) {
      case 'us':
      case 'uk':
        return speed * 0.621371;  // to mph
      case 'si':
        return speed * 0.277778;  // to m/s
      default:
        return speed;             // keep as km/h (default and 'ca')
    }
  },

  // Get formatted units
  getUnits: (unit: string) => ({
    temperature: unit === 'us' ? '°F' : '°C',
    windSpeed: {
      'us': 'mph',
      'uk': 'mph',
      'ca': 'km/h',
      'si': 'm/s'
    }[unit] || 'km/h',
    pressure: 'mb',
    visibility: 'm'
  }),

  // Format precipitation probability
  formatPrecipitation: (probability: number): string => {
    return probability > 3 ? `${probability}%` : '0%';
  },

  // Get weather background based on time of day
  getWeatherBackground: (icon: string, timeOfDay: string): string => {
    const gradients = {
      day: 'linear-gradient(135deg, #88d0ff 0%, #4da5ff 100%)', // Light blue for day
      night: 'linear-gradient(135deg, #1a2b50 0%, #2d4373 100%)', // Dark blue for night
    };

    return gradients[timeOfDay] || gradients.day;
  },

  // Get weather type from icon
  getWeatherType: (icon: string): string => {
    if (icon.includes('cloud')) return 'cloudy';
    if (icon.includes('rain') || icon.includes('drizzle')) return 'rain';
    if (icon.includes('snow')) return 'snow';
    return 'clear';
  },

  // Format wind direction
  formatWindDirection: (degrees: number): string => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(((degrees + 11.25) % 360) / 22.5);
    return directions[index];
  },

  // Format visibility (data comes in meters)
  formatVisibility: (visibility: number, unit: string): string => {
    const visibilityKm = visibility / 1000;  // Convert meters to kilometers

    if (unit === 'us' || unit === 'uk') {
      const visibilityMiles = visibilityKm * 0.621371;
      return visibilityMiles < 1 ? 'Poor' :
             visibilityMiles < 3 ? 'Moderate' :
             visibilityMiles < 7 ? 'Good' :
             'Excellent';
    }

    // For si, ca, and default
    return visibilityKm < 1 ? 'Poor' :
           visibilityKm < 5 ? 'Moderate' :
           visibilityKm < 10 ? 'Good' :
           'Excellent';
  },

  // Get weather description
  getWeatherDescription: (temp: number, icon: string, precipitation: number): string => {
    const weatherType = WeatherUtils.getWeatherType(icon);
    const tempDescription = temp < 32 ? 'freezing' :
                          temp < 50 ? 'cold' :
                          temp < 70 ? 'mild' :
                          temp < 85 ? 'warm' : 'hot';

    const precipDescription = precipitation > 0 ?
                            `with ${precipitation}% chance of precipitation` :
                            'with clear skies';

    return `${tempDescription} and ${weatherType} ${precipDescription}`;
  }
};

const styles = {
  container: {
    backgroundColor: '#f8fafc',
    minHeight: '100vh',
    padding: '0rem 0',
  },
  card: {
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    backgroundColor: '#ffffff',
  },
  mainWeatherCard: {
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  mainTemp: {
    fontSize: '3.5rem',
    fontWeight: 500,
    color: 'white',
    lineHeight: '1',
    marginBottom: '0.75rem',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  feelsLike: {
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '1rem',
  },
  weatherIcon: {
    fontSize: '1.2rem',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '1rem',
  },
  weatherLocation: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: 'white',
    marginBottom: '1rem',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  cardTitle: {
    fontSize: '1.1rem',
    fontWeight: 500,
    color: '#1a2b3b',
    marginBottom: '1.25rem',
  },
  detailLabel: {
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '0.25rem',
  },
  detailValue: {
    color: '#1a2b3b',
    fontSize: '1rem',
    fontWeight: 500,
  },
  select: {
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    padding: '8px 12px',
    fontSize: '0.9rem',
    color: '#1a2b3b',
    backgroundColor: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  detailBox: {
    padding: '0.75rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  sunTimesContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '1rem',
    marginTop: '1.25rem',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  commentarySection: {
    marginBottom: '1.5rem',
  },
  commentaryTitle: {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#1a2b3b',
    marginBottom: '0.5rem',
  },
  commentaryText: {
    fontSize: '0.95rem',
    color: '#64748b',
    lineHeight: '1.5',
  },
  lastUpdated: {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '1rem',
  },
  iconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#64748b',
    fontSize: '0.95rem',
  },
  timeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'rgba(255,255,255,0.9)',
    fontSize: '1.1rem',
    marginBottom: '1rem',
  },
  weatherIconLarge: {
    position: 'absolute' as const,
    top: '50%',
    right: '3rem',
    transform: 'translateY(-50%)',
    fontSize: '12rem',
    color: 'rgba(255,255,255,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '200px',
      height: '200px',
    },
  },
  mainWeatherContent: {
    position: 'relative' as const,
    maxWidth: '50%',
  },
  divider: {
    margin: '1.5rem 0',
    height: '1px',
    background: 'linear-gradient(to right, rgba(0,0,0,0.05), rgba(0,0,0,0.1), rgba(0,0,0,0.05))',
  },
  commentaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  commentaryIcon: {
    color: '#64748b',
    fontSize: '1.2rem',
  },
  timeSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '1.5rem',
  },
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
  },
  tempRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: 'rgba(255,255,255,0.9)',
    fontSize: '1.1rem',
    marginBottom: '1rem',
  },
  tempHigh: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  tempLow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    color: 'rgba(255,255,255,0.7)',
  },
  hourlyCard: {
    marginBottom: '1.5rem',
  },
  hourlyScroll: {
    display: 'flex',
    overflowX: 'auto',
    gap: '1rem',
    padding: '0.5rem 0.25rem',
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 transparent',
    '&::-webkit-scrollbar': {
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: '3px',
    },
  },
  hourlyItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    minWidth: '80px',
    padding: '0.75rem 0.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid rgba(0,0,0,0.05)',
  },
  hourlyTime: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: '0.5rem',
  },
  hourlyIcon: {
    color: '#1a2b3b',
    fontSize: '1.25rem',
    marginBottom: '0.5rem',
  },
  hourlyTemp: {
    fontSize: '1.1rem',
    fontWeight: 500,
    color: '#1a2b3b',
  },
  hourlyDetail: {
    fontSize: '0.8rem',
    color: '#64748b',
  },
  dailyCard: {
    // marginTop: 'rem',
  },
  dailyTable: {
    width: '100%',
    borderSpacing: '0',
    borderCollapse: 'separate',
    borderRadius: '8px',
  },
  dailyRow: {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  dailyCell: {
    padding: '1rem',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    color: '#1a2b3b',
    verticalAlign: 'middle',
  },
  dailyDay: {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#1a2b3b',
    width: '120px',
  },
  dailyIcon: {
    width: '48px',
    color: '#64748b',
    textAlign: 'center' as const,
  },
  dailyCondition: {
    fontSize: '0.95rem',
    color: '#64748b',
  },
  dailyTemp: {
    fontSize: '0.95rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  },
  dailyTempHigh: {
    color: '#1a2b3b',
    display: 'inline-flex',
    alignItems: 'center',
  },
  dailyTempLow: {
    color: '#64748b',
    marginLeft: '1rem',
    display: 'inline-flex',
    alignItems: 'center',
  },
  dailyPrecip: {
    fontSize: '0.9rem',
    color: '#64748b',
    textAlign: 'right' as const,
    width: '100px',
  },
  historicalTable: {
    width: '100%',
    borderSpacing: '0',
    borderCollapse: 'separate',
    borderRadius: '8px',
  },
  historicalCell: {
    padding: '1rem',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    color: '#1a2b3b',
    verticalAlign: 'middle',
    fontSize: '0.95rem',
  },
  historicalMonth: {
    fontWeight: 500,
    color: '#1a2b3b',
    width: '120px',
  },
  historicalValue: {
    color: '#64748b',
    textAlign: 'right' as const,
  },
};

const HistoricalTable = ({ data, units }) => {
  // If no historical data is provided, don't render anything
  if (!data || (!data.avg_temp && !data.avg_precip && !data.avg_length_day)) {
    return null;
  }

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const convertTemp = (temp) => {
    if (temp == null) return '—';
    // Data comes in Celsius, only convert if units is 'us'
    return units === 'us' ?
      `${(temp * 9/5 + 32).toFixed(1)}°` :
      `${temp.toFixed(1)}°`;
  };

  const formatPrecip = (precip) => {
    if (precip == null) return '—';
    return precip.toFixed(1);
  };

  const formatDayLength = (days) => {
    if (days == null) return '—';
    return days.toFixed(1);
  };

  // Check if we have any data to display
  const hasTemps = data.avg_temp && data.avg_temp.some(t => t != null);
  const hasPrecip = data.avg_precip && data.avg_precip.some(p => p != null);
  const hasDayLength = data.avg_length_day && data.avg_length_day.some(d => d != null);

  if (!hasTemps && !hasPrecip && !hasDayLength) {
    return null;
  }

  return (
    <div className="card" style={styles.card}>
      <div className="card-body p-4">
        <h5 style={styles.cardTitle}>Historical Monthly Averages</h5>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.historicalTable}>
            <thead>
              <tr>
                <th style={{...styles.historicalCell, ...styles.historicalMonth}}>Metric</th>
                {months.map(month => (
                  <th key={month} style={{...styles.historicalCell, ...styles.historicalValue, whiteSpace: 'nowrap'}}>
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hasTemps && (
                <tr>
                  <td style={{...styles.historicalCell, ...styles.historicalMonth}}>
                    Temperature {units === 'us' ? '(°F)' : '(°C)'}
                  </td>
                  {data.avg_temp.map((temp, idx) => (
                    <td key={idx} style={{...styles.historicalCell, ...styles.historicalValue}}>
                      {convertTemp(temp)}
                    </td>
                  ))}
                </tr>
              )}
              {hasPrecip && (
                <tr>
                  <td style={{...styles.historicalCell, ...styles.historicalMonth}}>
                    Precipitation (mm)
                  </td>
                  {data.avg_precip.map((precip, idx) => (
                    <td key={idx} style={{...styles.historicalCell, ...styles.historicalValue}}>
                      {formatPrecip(precip)}
                    </td>
                  ))}
                </tr>
              )}
              {hasDayLength && (
                <tr>
                  <td style={{...styles.historicalCell, ...styles.historicalMonth}}>
                    Day Length (hours)
                  </td>
                  {data.avg_length_day.map((dayLength, idx) => (
                    <td key={idx} style={{...styles.historicalCell, ...styles.historicalValue}}>
                      {formatDayLength(dayLength)}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [location, setLocation] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [historical, setHistorical] = useState(null);
  const [region, setRegion] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('units') || 'us';
  });

  useEffect(() => {
    const locationElement = document.getElementById('location');
    if (locationElement) {
      const location = JSON.parse(locationElement.getAttribute('data')!);
      setLocation(location);
    }

    const dataElement = document.getElementById('data');
    if (dataElement) {
      const data = JSON.parse(dataElement.getAttribute('data')!);
      setWeatherData(data);
    }

    const historicalElement = document.getElementById('historical');
    if (historicalElement) {
      const data = JSON.parse(historicalElement.getAttribute('data')!);
      setHistorical(data);
    }
  }, []);

  useEffect(() => {
    feather.replace();
  });

  if (!weatherData) {
    return <div>Loading...</div>;
  }

  const { map, current_main, current_ancillary } = weatherData;

  const mapSrc = map ? `https://www.openstreetmap.org/export/embed.html?bbox=${map.lon - 0.05},${map.lat - 0.05},${map.lon + 0.05},${map.lat + 0.05}&layer=mapnik&marker=${map.lat},${map.lon}&locale=${navigator.language}` : '';

  const units = WeatherUtils.getUnits(region);
  const temperature = WeatherUtils.convertTemperature(current_main.temperature, region).toFixed(0);
  const apparentTemperature = WeatherUtils.convertTemperature(current_main.apparent_temperature, region).toFixed(0);
  const windSpeed = WeatherUtils.convertWindSpeed(current_ancillary.wind_speed, region).toFixed(1);
  const windDirection = WeatherUtils.formatWindDirection(current_ancillary.wind_direction);
  const visibility = WeatherUtils.formatVisibility(current_ancillary.visibility, region);

  const timeOfDay = TimeUtils.getTimeOfDay(
    current_ancillary.sunrise_time,
    current_ancillary.sunset_time,
    current_ancillary.tz_offset
  );
  const backgroundStyle = WeatherUtils.getWeatherBackground(current_main.raw_icon, timeOfDay);

  return (
    <div style={styles.container}>
      <div className="container">
        <div className="d-flex justify-content-end mb-4">
          <select
            style={styles.select}
            value={region}
            onChange={(e) => {
              setRegion(e.target.value);
              // Update URL with new units
              const url = new URL(window.location.href);
              url.searchParams.set('units', e.target.value);
              window.history.pushState({}, '', url.toString());
            }}
          >
            <option value="us">US (°F, mph)</option>
            <option value="ca">CA (°C, km/h)</option>
            <option value="uk">UK (°C, mph)</option>
            <option value="si">SI (°C, m/s)</option>
          </select>
        </div>

        <div className="row g-4">
          <div className="col-md-7">
            <div className="card h-100" style={{
              ...styles.mainWeatherCard,
              background: backgroundStyle,
            }}>
              <div className="card-body p-4">
                <div style={styles.mainWeatherContent}>
                  <div style={styles.weatherLocation}>{[location.name_level_four, location.name_level_three, location.name_level_two, location.name_level_one].filter(Boolean).join(', ')}</div>

                  <div style={styles.timeSection}>
                    <div style={styles.timeRow}>
                      <i data-feather="map-pin"></i>
                      <span>
                        {TimeUtils.formatTime(TimeUtils.getCurrentLocationTime(current_ancillary.tz_offset))}
                        {' '}({current_ancillary.tz})
                      </span>
                    </div>
                  </div>

                  <div style={styles.mainTemp}>{temperature}{units.temperature}</div>
                  <div style={styles.feelsLike}>Feels like {apparentTemperature}{units.temperature}</div>

                  <div style={styles.tempRange}>
                    <div style={styles.tempHigh}>
                      <i data-feather="arrow-up"></i>
                      {WeatherUtils.convertTemperature(current_main.temperature_high, region).toFixed(0)}{units.temperature}
                    </div>
                    <div style={styles.tempLow}>
                      <i data-feather="arrow-down"></i>
                      {WeatherUtils.convertTemperature(current_main.temperature_low, region).toFixed(0)}{units.temperature}
                    </div>
                  </div>
                </div>

                <div style={styles.weatherIconLarge}>
                  <i data-feather={current_main.icon} width="96" height="96" />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="card h-100" style={styles.card}>
              <div className="card-body p-4">
                <h5 style={styles.cardTitle}>Current Conditions</h5>
                <div style={styles.detailsGrid}>
                  <div style={styles.detailBox}>
                    <div style={styles.detailLabel}>
                      <i data-feather="wind" style={{ marginRight: '0.5rem' }} />
                      Wind
                    </div>
                    <div style={styles.detailValue}>{windSpeed} {units.windSpeed}</div>
                  </div>
                  <div style={styles.detailBox}>
                    <div style={styles.detailLabel}>
                      <i data-feather="droplet" style={{ marginRight: '0.5rem' }} />
                      Humidity
                    </div>
                    <div style={styles.detailValue}>{(current_ancillary.humidity * 100).toFixed(0)}%</div>
                  </div>
                  <div style={styles.detailBox}>
                    <div style={styles.detailLabel}>
                      <i data-feather="thermometer" style={{ marginRight: '0.5rem' }} />
                      Pressure
                    </div>
                    <div style={styles.detailValue}>{current_ancillary.pressure} {units.pressure}</div>
                  </div>
                  <div style={styles.detailBox}>
                    <div style={styles.detailLabel}>
                      <i data-feather="eye" style={{ marginRight: '0.5rem' }} />
                      Visibility
                    </div>
                    <div style={styles.detailValue}>{visibility}</div>
                  </div>
                </div>

                <div style={styles.sunTimesContainer}>
                  <div className="row">
                    <div className="col-6">
                      <div style={styles.detailLabel}>
                        <i data-feather="sunrise" style={{ marginRight: '0.5rem' }} />
                        Sunrise
                      </div>
                      <div style={styles.detailValue}>
                        {TimeUtils.formatTime(TimeUtils.toLocationTime(current_ancillary.sunrise_time, current_ancillary.tz_offset))}
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={styles.detailLabel}>
                        <i data-feather="sunset" style={{ marginRight: '0.5rem' }} />
                        Sunset
                      </div>
                      <div style={styles.detailValue}>
                        {TimeUtils.formatTime(TimeUtils.toLocationTime(current_ancillary.sunset_time, current_ancillary.tz_offset))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100" style={styles.card}>
              <div className="card-body p-4">
                <h5 style={styles.cardTitle}>Forecast</h5>
                <div style={styles.commentarySection}>
                  <div style={styles.commentaryHeader}>
                    <i data-feather="sun" style={styles.commentaryIcon} />
                    <h6 style={styles.commentaryTitle}>Today</h6>
                  </div>
                  <p style={styles.commentaryText}>{weatherData.commentary.today[region]}</p>
                </div>

                <div style={styles.divider} />

                <div style={styles.commentarySection}>
                  <div style={styles.commentaryHeader}>
                    <i data-feather="calendar" style={styles.commentaryIcon} />
                    <h6 style={styles.commentaryTitle}>Tomorrow</h6>
                  </div>
                  <p style={styles.commentaryText}>{weatherData.commentary.tomorrow[region]}</p>
                </div>

                {weatherData.commentary.historical?.[region] && (
                  <>
                    <div style={styles.divider} />
                    <div style={styles.commentarySection}>
                      <div style={styles.commentaryHeader}>
                        <i data-feather="trending-up" style={styles.commentaryIcon} />
                        <h6 style={styles.commentaryTitle}>Historical Patterns</h6>
                      </div>
                      <p style={styles.commentaryText}>{weatherData.commentary.historical[region]}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100" style={styles.card}>
              <div className="card-body p-4">
                <h5 style={styles.cardTitle}>Location</h5>
                <div style={{
                  height: '400px',
                  width: '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {map ? (
                    <iframe
                      title="Weather Location Map"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={mapSrc}
                      allowFullScreen
                    />
                  ) : (
                    <div>Map data is not available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card" style={styles.card}>
              <div className="card-body p-4">
                <h5 style={styles.cardTitle}>Hourly Forecast</h5>
                <div style={styles.hourlyScroll}>
                  {weatherData.hourly.map((hour, index) => (
                    <HourlyItem
                      // @ts-ignore
                      key={`hour-${index}`}
                      hour={hour.time}
                      icon={hour.icon}
                      temp={WeatherUtils.convertTemperature(hour.temperature, region)}
                      detail={WeatherUtils.formatPrecipitation(hour.precipitation_probability || 0)}
                      timezone_offset={current_ancillary.tz_offset}
                      getTemperatureUnit={() => units.temperature}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card" style={{...styles.card, ...styles.dailyCard}}>
              <div className="card-body p-4">
                <h5 style={styles.cardTitle}>7-Day Forecast</h5>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.dailyTable}>
                    <tbody>
                      {weatherData.daily.map((day, index) => (
                        <DailyRow
                          // @ts-ignore
                          key={`day-${index}`}
                          day={TimeUtils.formatDay(TimeUtils.toLocationTime(day.time, current_ancillary.tz_offset))}
                          date={TimeUtils.formatDate(TimeUtils.toLocationTime(day.time, current_ancillary.tz_offset))}
                          icon={day.icon}
                          condition={day.condition}
                          tempHigh={WeatherUtils.convertTemperature(day.temperature_high, region).toFixed(0)}
                          tempLow={WeatherUtils.convertTemperature(day.temperature_low, region).toFixed(0)}
                          precipitation={day.precipitation_probability}
                          units={units}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {historical && (
            <div className="col-12">
              <HistoricalTable data={historical} units={region} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const HourlyItem = ({
  hour,
  icon,
  temp,
  detail,
  timezone_offset,
  getTemperatureUnit
}) => {
  const date = TimeUtils.toLocationTime(hour, timezone_offset);
  const timeString = TimeUtils.formatHour(date);

  return (
    <div style={styles.hourlyItem}>
      <div style={styles.hourlyTime}>{timeString}</div>
      <div style={styles.hourlyIcon}>
        <i data-feather={icon} />
      </div>
      <div style={styles.hourlyTemp}>
        {temp.toFixed(0)}{getTemperatureUnit()}
      </div>
      <div style={styles.hourlyDetail}>{detail}</div>
    </div>
  );
};

const DailyRow = ({
  day,
  date,
  icon,
  condition,
  tempHigh,
  tempLow,
  precipitation,
  units,
}) => (
  <tr style={styles.dailyRow}>
    <td style={{...styles.dailyCell, ...styles.dailyDay}}>
      <div style={{fontWeight: 500}}>{day}</div>
      <div style={{fontSize: '0.85rem', color: '#64748b'}}>{date}</div>
    </td>
    <td style={{...styles.dailyCell, ...styles.dailyIcon}}>
      <i data-feather={icon} />
    </td>
    <td style={{...styles.dailyCell, ...styles.dailyCondition}}>
      {condition}
    </td>
    <td style={{...styles.dailyCell, ...styles.dailyTemp}}>
      <span style={styles.dailyTempHigh}>
        <i data-feather="arrow-up" style={{ width: '16px', height: '16px', marginRight: '2px' }} />
        {tempHigh}{units.temperature}
      </span>
      <span style={styles.dailyTempLow}>
        <i data-feather="arrow-down" style={{ width: '16px', height: '16px', marginRight: '2px' }} />
        {tempLow}{units.temperature}
      </span>
    </td>
    <td style={{...styles.dailyCell, ...styles.dailyPrecip}}>
      {precipitation > 3 ? `${precipitation}%` : ''}
    </td>
  </tr>
);

export default () => {
  createRoot(document.getElementById('app')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}